import { Plugin, PluginKey, type EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import type { EditorContext } from "../context";
import { isInCode } from "../schema";

/** The three menus that share the trigger machinery (`editor.md` §2). */
export type SuggestionType = "slash" | "emoji" | "mention" | "document";

export interface SuggestionState {
  type: SuggestionType;
  query: string;
  /** Position of the trigger character. */
  from: number;
  /** Caret position — the end of the query. */
  to: number;
}

interface PluginState {
  active: SuggestionState | null;
  /** Set by Escape; cleared when the caret leaves the trigger run. */
  dismissedAt: number | null;
}

export const suggestionKey = new PluginKey<PluginState>("wikiSuggestion");

interface Trigger {
  type: SuggestionType;
  /** Matches the run ending at the caret; group 1 is the query. */
  match: RegExp;
  /** Characters the trigger may follow. */
  triggerLength: number;
}

const TRIGGERS: Trigger[] = [
  { type: "document", match: /\[\[([^[\]\n]*)$/, triggerLength: 2 },
  { type: "slash", match: /(?:^|\s)\/([\w-]*)$/, triggerLength: 1 },
  { type: "emoji", match: /(?:^|\s):([\w+-]+)$/, triggerLength: 1 },
  { type: "mention", match: /(?:^|\s)@([\w'.-]*(?: [\w'.-]+)*)$/, triggerLength: 1 },
];

/** The text of the current block up to the caret. */
function textBeforeCaret(state: EditorState): { text: string; start: number } | null {
  const { $from, empty } = state.selection;
  if (!empty || !$from.parent.isTextblock) return null;
  const start = $from.start();
  return { text: $from.parent.textBetween(0, $from.pos - start, "\n", "￼"), start };
}

export function detectSuggestion(state: EditorState): SuggestionState | null {
  if (isInCode(state)) return null;
  const before = textBeforeCaret(state);
  if (!before) return null;
  for (const trigger of TRIGGERS) {
    const found = trigger.match.exec(before.text);
    const query = found?.[1];
    if (!found || query === undefined) continue;
    const queryStart = before.text.length - query.length;
    const from = before.start + queryStart - trigger.triggerLength;
    return { type: trigger.type, query, from, to: state.selection.from };
  }
  return null;
}

/**
 * A handler the open menu registers so it can take the arrow keys and Enter
 * while it is showing. It is a mutable box rather than a prop because the
 * plugin is created once and the component re-renders constantly.
 */
export interface SuggestionBridge {
  onKeyDown: ((event: KeyboardEvent) => boolean) | null;
}

export function createSuggestionBridge(): SuggestionBridge {
  return { onKeyDown: null };
}

export function suggestionPlugin(
  context: EditorContext,
  bridge: SuggestionBridge,
): Plugin<PluginState> {
  return new Plugin<PluginState>({
    key: suggestionKey,
    state: {
      init: () => ({ active: null, dismissedAt: null }),
      apply(tr, value, _old, newState) {
        if (context.readOnly) return { active: null, dismissedAt: null };
        const dismiss = tr.getMeta(suggestionKey) as { dismiss?: boolean } | undefined;
        const active = detectSuggestion(newState);
        if (dismiss?.dismiss === true) {
          return { active: null, dismissedAt: active ? active.from : null };
        }
        const dismissedAt =
          value.dismissedAt !== null && active?.from === value.dismissedAt
            ? value.dismissedAt
            : null;
        return { active: dismissedAt === null ? active : null, dismissedAt };
      },
    },
    props: {
      handleKeyDown(_view, event) {
        if (event.key === "Escape") {
          const state = suggestionKey.getState(_view.state);
          if (state?.active) {
            dismissSuggestion(_view);
            return true;
          }
        }
        return bridge.onKeyDown?.(event) ?? false;
      },
    },
  });
}

export function activeSuggestion(state: EditorState): SuggestionState | null {
  return suggestionKey.getState(state)?.active ?? null;
}

export function dismissSuggestion(view: EditorView): void {
  view.dispatch(view.state.tr.setMeta(suggestionKey, { dismiss: true }));
}

/** Remove the trigger and its query, then run the item's own insertion. */
export function replaceSuggestion(
  view: EditorView,
  suggestion: SuggestionState,
  insert: (view: EditorView) => void,
): void {
  const tr = view.state.tr.delete(suggestion.from, suggestion.to);
  tr.setMeta(suggestionKey, { dismiss: true });
  view.dispatch(tr);
  insert(view);
  view.focus();
}
